import { DestroyRef, inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ListingMarkupsGroup, PricesAndFees, PricesAndFeesGroups, RatePlanGroup } from '@app/shared/models/tax';
import { PropertiesService, RatePlan } from '@app/shared/services/properties/properties.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { BehaviorSubject, combineLatest, throwError, timer, Observable } from 'rxjs';
import { catchError, delay, filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { UserPermissionsService } from '@app/core/user-permissions/user-permissions.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MarkupsService, Markup } from '@app/features/pricing-and-availability/services/markups/markups.service';
import { humanize } from '@app/shared/utils/lib/general.util';

const PRICE_SAVE_STATE_KEYS = [RatePlanGroup, ListingMarkupsGroup, ...PricesAndFeesGroups];
enum PriceSaveState {
  NOT_SAVED = 0,
  SAVING = 1,
  SAVED = 2,
  ERROR = -1,
}

interface PricingChange {
  key: string;
  data: {
    value: number | null;
    [key: string]: unknown;
  };
}

// Property pricing state service
@Injectable({
  providedIn: 'any',
})
export class PropertyPricingService {
  private destroyRef: DestroyRef = inject(DestroyRef);
  public savedPricingFeeDiscount$ = new BehaviorSubject(null);
  private propertyId$ = new BehaviorSubject<string | null>(null);
  public get propertyId() {
    return this.propertyId$.value;
  }
  public loadingPricingFeeDiscount$ = new BehaviorSubject(false);
  public pricingFeeDiscountData$ = this.savedPricingFeeDiscount$.pipe(
    tap(() => this.loadingPricingFeeDiscount$.next(true)),
    switchMap(() => this.propertiesService.fetchPropertyFeesDiscounts(this.propertyId)),
    tap((res: any) => {
      this.loadingPricingFeeDiscount$.next(false);
      if (res.error) {
        this.toast.error('There was a problem fetching Fee & Discount data for this property.');
        throwError(res.err);
      }
    }),
    map((res) => {
      const updated = { ...res };

      // Convert base price from cents
      if (res.base_price?.value) {
        updated.base_price.value = res.base_price.value / 100;
      }
      // Convert weekend base price from cents
      if (res.weekend_price.value) {
        updated.weekend_price.value = res.weekend_price.value / 100;
      }
      return updated;
    }),
    shareReplay(1)
  );

  public priceSaveState$: BehaviorSubject<Record<(typeof PRICE_SAVE_STATE_KEYS)[0], PriceSaveState>> =
    new BehaviorSubject({
      primaryRatePlan: PriceSaveState.NOT_SAVED,
      ...PricesAndFeesGroups.reduce((acc, key) => ({ ...acc, [key]: PriceSaveState.NOT_SAVED }), {}),
    });

  public savedBookingRatePlan$ = new BehaviorSubject(null);
  public bookingRatePlanData$ = this.savedBookingRatePlan$.pipe(
    switchMap(() => this.userPermissionsService.isPrimaryUser()),
    filter(Boolean),
    switchMap(() => this.propertiesService.getBookingRatePlans(this.propertyId)),
    map((res) => {
      // could 404 if there are no rate plans, which is expected
      if (res.error) {
        return null;
      }
      return res;
    }),
    shareReplay(1)
  );

  public savedListingMarkups$ = new BehaviorSubject(null);

  public markupData$ = this.savedListingMarkups$.pipe(
    switchMap(() =>
      combineLatest([this.markupsService.getPropertyMarkups(this.propertyId), this.markupsService.getPlatformMarkups()])
    ),
    map(([propertyMarkups, platformMarkups]) => ({
      propertyMarkups,
      platformMarkups,
    })),
    shareReplay(1)
  );

  public securityDepositSubline$ = this.pricingFeeDiscountData$.pipe(
    map((data) => `Airbnb ${data.security_deposit.value ?? 0}, Direct ${data.direct_security_deposit.value ?? 0}`),
    shareReplay(1)
  );

  public occupancyBasedSubline$ = this.pricingFeeDiscountData$.pipe(
    map((data) => {
      const label = PricesAndFees.occupancyBased
        .map((occupancy) => {
          if (!data[occupancy.key]?.value) return null;
          return `${occupancy.label} ${data[occupancy.key]?.value ?? 0} ${occupancy.key === 'price_per_extra_person' ? '' : 'guests'}`;
        })
        .filter(Boolean)
        .join(', ');
      return label?.length ? label : 'None';
    }),
    shareReplay(1)
  );

  public discountSubline$ = this.pricingFeeDiscountData$.pipe(
    map((data) => {
      const label = PricesAndFees.discounts
        .map((discount) => {
          if (!data[discount.key]?.value) return null;
          return `${discount.label} ${data[discount.key]?.value ?? 0}`;
        })
        .filter(Boolean)
        .join(', ');
      return label?.length ? label : 'None';
    }),
    shareReplay(1)
  );

  public feesSubline$ = this.pricingFeeDiscountData$.pipe(
    map((data) => {
      const label = PricesAndFees.fees
        .map((fee) => {
          if (!data[fee.key]?.value) return null;
          return `${fee.label} ${data[fee.key]?.value ?? 0}`;
        })
        .filter(Boolean)
        .join(', ');
      return label?.length ? label : 'None';
    }),
    shareReplay(1)
  );

  public primaryRatePlanSubline$ = this.bookingRatePlanData$.pipe(
    map((data) => {
      if (!data) {
        return null;
      }
      const label = data
        .reduce((acc, listing) => {
          const primaryRatePlan = listing.rate_plans.find((x) => x.is_primary);
          // match conditions in the component where we only show this if there are multiple rate plans
          if (primaryRatePlan && listing.rate_plans.length > 1) {
            acc.push(primaryRatePlan.name);
          }
          return acc;
        }, [])
        .join(', ');

      return label?.length ? label : null;
    }),
    shareReplay(1)
  );

  public listingMarkupsSubline$ = this.markupData$.pipe(
    map((data) => {
      const label = data.platformMarkups.map((markup) => `${humanize(markup.platform)} ${markup.markup}`).join(', ');
      return label?.length ? label : null;
    }),
    shareReplay(1)
  );

  constructor(
    private route: ActivatedRoute,
    private propertiesService: PropertiesService,
    private toast: ToastNotificationsService,
    private userPermissionsService: UserPermissionsService,
    private markupsService: MarkupsService
  ) {
    this.init();
  }

  private init() {
    this.route.paramMap
      .pipe(
        filter((params) => params.has('id')),
        map((params) => params.get('id')),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((propertyId) => {
        this.propertyId$.next(propertyId);
      });
  }

  /**
   * Save price changes for fees
   * Can handle a single field or an array of fields
   * @param changes
   */
  public savePriceChanges(changes: PricingChange[] | PricingChange) {
    // Normalize input to always be an array
    const normalizedChanges = Array.isArray(changes) ? changes : [changes];

    // Copy the data for each change to avoid mutating template data
    const updatedChanges = normalizedChanges.map(({ key, data }) => {
      const updatedData = { ...data };

      // Convert base price and weekend price to cents
      if (key === 'base_price' || key === 'weekend_price') {
        updatedData.value = data.value * 100;

        if (updatedData.value === 0) {
          updatedData.value = null;
        }
      }

      return { key, data: updatedData };
    });

    // Choose the appropriate service call based on number of changes
    const saveOperation$ =
      updatedChanges.length === 1
        ? this.propertiesService.updatePropertyFeesDiscounts(
            this.propertyId,
            updatedChanges[0].key,
            updatedChanges[0].data
          )
        : this.propertiesService.updateMultiplePropertyFeesDiscounts(this.propertyId, updatedChanges);

    this.handleOperationWithSaveStatus(
      saveOperation$,
      updatedChanges[0].key,
      () => this.savedPricingFeeDiscount$.next(updatedChanges),
      () => {
        this.toast.error('There was a problem saving the Fee & Discount data for this property.');
      }
    );
  }

  public saveBookingRatePlan(listingId: string, payload: RatePlan) {
    const saveOperation$ = this.propertiesService.updateRatePlan(this.propertyId, listingId, payload);

    this.handleOperationWithSaveStatus(
      saveOperation$,
      RatePlanGroup,
      () => this.savedBookingRatePlan$.next(payload),
      () => {
        this.toast.error('There was a problem saving the rate plan.');
      }
    );
  }

  public saveListingMarkups(payload: Markup) {
    const saveOperation$ = this.markupsService.updatePropertyMarkups(this.propertyId, payload);

    this.handleOperationWithSaveStatus(
      saveOperation$,
      ListingMarkupsGroup,
      () => this.savedListingMarkups$.next(payload),
      () => {
        this.toast.error('There was a problem saving the listing markups.');
      }
    );
  }

  private handleOperationWithSaveStatus<T>(
    operation$: Observable<T>,
    stateKey: string,
    successCallback: (result: T) => void,
    errorCallback: () => void
  ): void {
    this.updateSaveState(stateKey, PriceSaveState.SAVING);

    operation$
      .pipe(
        tap((result) => {
          this.updateSaveState(stateKey, PriceSaveState.SAVED);
          successCallback(result);
        }),
        delay(2000),
        catchError(() => {
          this.updateSaveState(stateKey, PriceSaveState.ERROR);
          errorCallback();
          return timer(5000).pipe(take(1));
        })
      )
      .subscribe({
        next: () => {
          this.updateSaveState(stateKey);
        },
        complete: () => {
          this.updateSaveState(stateKey);
        },
      });
  }

  private updateSaveState(fieldKey: string, status: PriceSaveState = PriceSaveState.NOT_SAVED) {
    // look for the key directly in the group, if not found, look for it in the PricesAndFees object
    const group =
      PRICE_SAVE_STATE_KEYS.find((x) => x === fieldKey) ??
      Object.entries(PricesAndFees).find(([_, fields]) => fields.find((field) => field.key === fieldKey))?.[0];

    this.priceSaveState$.next({
      ...this.priceSaveState$.value,
      [group]: status,
    });
  }
}
